import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  deleteSamplingAreas,
  deleteSamplingAreasByFarm,
} from '../../services/farmMapping/samplingAreas/deleteSamplingAreas'
import {
  getSamplingAreasByFarm,
  getUncroppedFeatures,
} from '../../services/farmMapping/samplingAreas/getSamplingAreas'
import {
  createSamplingAreasFeatureByFarm,
  updateSamplingAreasFeatureByFarm,
} from '../../services/farmMapping/samplingAreas/saveSamplingAreas'
import type { SamplingArea, ToGeoJSON } from '../../types/apiModels'
import { queryKeys } from './queryKeys'

// Declared at module level on purpose: React Query memoises the projection against the
// select identity, so an inline arrow would rebuild `data` on every render and re-fire the
// consumer effects that watch it. The clone keeps map consumers — which mutate the features
// they draw — from writing straight into the cached entities shared by every other reader.
// The uncropped projection is optional on the entity, and every consumer reads
// `properties.id` off each feature, so the holes are dropped rather than handed out.
const selectUncroppedFeatures = (areas: SamplingArea[]): ToGeoJSON[] =>
  structuredClone(
    areas
      .map(area => area?.toUncroppedGeoJSON)
      .filter((feature): feature is ToGeoJSON => Boolean(feature)),
  )

const selectCroppedEntities = (areas: SamplingArea[]): SamplingArea[] => structuredClone(areas)

// getUncroppedFeatures only shallow-copies each entity, so the projection still shares the
// cache's nested objects — the clone is what makes the mutation guarantee hold here too.
const selectUncroppedEntities = (areas: SamplingArea[]) =>
  structuredClone(getUncroppedFeatures({ features: areas }))

type SamplingAreaFeaturesInput = { features: ToGeoJSON[] }

// Single source of truth for the farm strata read: the three view hooks spread it and add
// their own projection, while the imperative readers pass it straight to the query client so
// every consumer shares one cache entry instead of racing its own fetch.
export const samplingAreasQueryOptions = (farmId: string | undefined) =>
  queryOptions({
    queryKey: queryKeys.samplingAreas(farmId),
    queryFn: ({ signal }) => getSamplingAreasByFarm({ farmId: farmId as string, signal }),
  })

export const useSamplingAreas = ({ farmId }: { farmId: string | undefined }) =>
  useQuery({
    ...samplingAreasQueryOptions(farmId),
    enabled: !!farmId,
    select: selectUncroppedFeatures,
  })

export const useCroppedSamplingAreas = ({ farmId }: { farmId: string | undefined }) =>
  useQuery({
    ...samplingAreasQueryOptions(farmId),
    enabled: !!farmId,
    select: selectCroppedEntities,
  })

export const useUncroppedSamplingAreas = ({ farmId }: { farmId: string | undefined }) =>
  useQuery({
    ...samplingAreasQueryOptions(farmId),
    enabled: !!farmId,
    select: selectUncroppedEntities,
  })

export const useCreateSamplingAreas = ({ farmId }: { farmId: string | undefined }) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ features }: SamplingAreaFeaturesInput) =>
      createSamplingAreasFeatureByFarm({ farmId: farmId as string, features }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.samplingAreas(farmId) }),
  })
}

// PUT /v1/samplingAreas answers message-only (no payload): the fresh list can only
// come from an invalidate-refetch, never from seeding the cache with the response.
export const useUpdateSamplingAreas = ({ farmId }: { farmId: string | undefined }) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ features }: SamplingAreaFeaturesInput) =>
      updateSamplingAreasFeatureByFarm({ farmId: farmId as string, features }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.samplingAreas(farmId) }),
  })
}

export const useDeleteSamplingAreas = ({ farmId }: { farmId: string | undefined }) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ entities }: { entities: { id: string }[] }) => deleteSamplingAreas({ entities }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.samplingAreas(farmId) }),
  })
}

export const useDeleteSamplingAreasByFarm = ({ farmId }: { farmId: string | undefined }) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => deleteSamplingAreasByFarm({ farmId: farmId as string }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.samplingAreas(farmId) }),
    // The wipe-all flow renders its own confirmation/error surface inline.
    meta: { suppressGlobalError: true },
  })
}
